// src/components/VacanciesBoard.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Briefcase,
  MapPin,
  Clock,
  DollarSign,
  Users,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Filter,
  Building2,
  Wifi,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Pause,
  XCircle,
  Star,
  Plus,
  Pencil,
  Trash2,
  X,
  Play,
  Archive,
} from 'lucide-react';
import {
  getVacancies,
  getVacancyStats,
  createVacancy,
  updateVacancy,
  updateVacancyStatus,
  deleteVacancy,
  type VacancyDTO,
} from '@/lib/api';

/* ─── Status config ─── */
const STATUS_CFG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  draft:     { label: 'Borrador',   color: 'text-gray-400',    bg: 'bg-gray-500/20',    icon: <Clock size={14} /> },
  open:      { label: 'Abierta',    color: 'text-emerald-400', bg: 'bg-emerald-500/20',  icon: <CheckCircle2 size={14} /> },
  paused:    { label: 'Pausada',    color: 'text-amber-400',   bg: 'bg-amber-500/20',    icon: <Pause size={14} /> },
  closed:    { label: 'Cerrada',    color: 'text-red-400',     bg: 'bg-red-500/20',      icon: <XCircle size={14} /> },
  filled:    { label: 'Cubierta',   color: 'text-blue-400',    bg: 'bg-blue-500/20',     icon: <Star size={14} /> },
  cancelled: { label: 'Cancelada',  color: 'text-red-300',     bg: 'bg-red-400/20',      icon: <XCircle size={14} /> },
};

/* Valid status transitions */
const VALID_TRANSITIONS: Record<string, { to: string; label: string; icon: React.ReactNode }[]> = {
  draft:     [{ to: 'open', label: 'Publicar', icon: <Play size={12} /> }, { to: 'cancelled', label: 'Cancelar', icon: <XCircle size={12} /> }],
  open:      [{ to: 'paused', label: 'Pausar', icon: <Pause size={12} /> }, { to: 'closed', label: 'Cerrar', icon: <Archive size={12} /> }, { to: 'filled', label: 'Cubierta', icon: <Star size={12} /> }, { to: 'cancelled', label: 'Cancelar', icon: <XCircle size={12} /> }],
  paused:    [{ to: 'open', label: 'Reabrir', icon: <Play size={12} /> }, { to: 'closed', label: 'Cerrar', icon: <Archive size={12} /> }, { to: 'cancelled', label: 'Cancelar', icon: <XCircle size={12} /> }],
  cancelled: [{ to: 'draft', label: 'Reactivar', icon: <RefreshCw size={12} /> }],
  closed:    [],
  filled:    [],
};

const REMOTE_LABELS: Record<string, string> = { onsite: 'Presencial', hybrid: 'Híbrido', remote: 'Remoto' };
const EMPLOYMENT_LABELS: Record<string, string> = { full_time: 'Tiempo completo', part_time: 'Medio tiempo', contract: 'Contrato', internship: 'Pasantía', freelance: 'Freelance' };

function formatSalary(v: VacancyDTO): string | null {
  if (!v.show_salary) return null;
  if (!v.salary_min && !v.salary_max) return null;
  const fmt = (n: number) => n.toLocaleString('es-MX');
  const cur = v.salary_currency || 'MXN';
  const per = v.salary_period === 'monthly' ? '/mes' : v.salary_period === 'yearly' ? '/año' : '/hr';
  if (v.salary_min && v.salary_max) return `${cur} $${fmt(v.salary_min)} - $${fmt(v.salary_max)} ${per}`;
  if (v.salary_min) return `${cur} $${fmt(v.salary_min)}+ ${per}`;
  return `Hasta ${cur} $${fmt(v.salary_max!)} ${per}`;
}

/* ─── Form Data type ─── */
type VacancyFormData = {
  title: string;
  description: string;
  department: string;
  requirements: string;
  skills_required: string;
  nice_to_have: string;
  salary_min: string;
  salary_max: string;
  salary_currency: string;
  salary_period: string;
  show_salary: boolean;
  location: string;
  remote_policy: string;
  employment_type: string;
  copy_tone: string;
  max_candidates: string;
};

const EMPTY_FORM: VacancyFormData = {
  title: '', description: '', department: '', requirements: '', skills_required: '',
  nice_to_have: '', salary_min: '', salary_max: '', salary_currency: 'MXN',
  salary_period: 'monthly', show_salary: true, location: '', remote_policy: 'hybrid',
  employment_type: 'full_time', copy_tone: 'profesional', max_candidates: '',
};

function vacancyToForm(v: VacancyDTO): VacancyFormData {
  return {
    title: v.title,
    description: v.description,
    department: v.department || '',
    requirements: (v.requirements || []).join('\n'),
    skills_required: (v.skills_required || []).join(', '),
    nice_to_have: (v.nice_to_have || []).join(', '),
    salary_min: v.salary_min ? String(v.salary_min) : '',
    salary_max: v.salary_max ? String(v.salary_max) : '',
    salary_currency: v.salary_currency || 'MXN',
    salary_period: v.salary_period || 'monthly',
    show_salary: v.show_salary,
    location: v.location || '',
    remote_policy: v.remote_policy || 'hybrid',
    employment_type: v.employment_type || 'full_time',
    copy_tone: v.copy_tone || 'profesional',
    max_candidates: v.max_candidates ? String(v.max_candidates) : '',
  };
}

function formToPayload(f: VacancyFormData) {
  return {
    title: f.title.trim(),
    description: f.description.trim(),
    department: f.department.trim() || undefined,
    requirements: f.requirements.trim() ? f.requirements.split('\n').map(s => s.trim()).filter(Boolean) : [],
    skills_required: f.skills_required.trim() ? f.skills_required.split(',').map(s => s.trim()).filter(Boolean) : [],
    nice_to_have: f.nice_to_have.trim() ? f.nice_to_have.split(',').map(s => s.trim()).filter(Boolean) : [],
    salary_min: f.salary_min ? Number(f.salary_min) : undefined,
    salary_max: f.salary_max ? Number(f.salary_max) : undefined,
    salary_currency: f.salary_currency,
    salary_period: f.salary_period,
    show_salary: f.show_salary,
    location: f.location.trim() || undefined,
    remote_policy: f.remote_policy,
    employment_type: f.employment_type,
    copy_tone: f.copy_tone,
    max_candidates: f.max_candidates ? Number(f.max_candidates) : undefined,
  };
}

/* ─── Vacancy Form Modal ─── */
function VacancyFormModal({
  initial,
  editId,
  onClose,
  onSaved,
}: {
  initial: VacancyFormData;
  editId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<VacancyFormData>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEdit = !!editId;

  const set = (k: keyof VacancyFormData, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = formToPayload(form);
      if (isEdit) {
        await updateVacancy(editId!, payload);
      } else {
        await createVacancy(payload as any);
      }
      onSaved();
    } catch (e: any) {
      setError(e.message || 'Error al guardar vacante');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none';
  const labelCls = 'text-white/60 text-xs block mb-1';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#1a1a2e] border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#1a1a2e] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-white font-semibold text-lg">{isEdit ? 'Editar Vacante' : 'Nueva Vacante'}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg text-sm">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {/* Row 1: Title + Department */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className={labelCls}>Título *</label>
              <input type="text" value={form.title} onChange={e => set('title', e.target.value)} required placeholder="Ej: Full Stack Developer" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Departamento</label>
              <input type="text" value={form.department} onChange={e => set('department', e.target.value)} placeholder="Ej: Ingeniería" className={inputCls} />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className={labelCls}>Descripción *</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} required rows={4} placeholder="Descripción detallada del puesto..." className={inputCls + ' resize-y'} />
          </div>

          {/* Requirements */}
          <div>
            <label className={labelCls}>Requisitos (uno por línea)</label>
            <textarea value={form.requirements} onChange={e => set('requirements', e.target.value)} rows={3} placeholder={"3+ años de experiencia\nTítulo universitario\nInglés avanzado"} className={inputCls + ' resize-y'} />
          </div>

          {/* Skills */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Skills requeridas (separadas por coma)</label>
              <input type="text" value={form.skills_required} onChange={e => set('skills_required', e.target.value)} placeholder="React, Node.js, Python" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Deseables (separadas por coma)</label>
              <input type="text" value={form.nice_to_have} onChange={e => set('nice_to_have', e.target.value)} placeholder="Docker, Kubernetes" className={inputCls} />
            </div>
          </div>

          {/* Salary */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
            <div>
              <label className={labelCls}>Salario mín</label>
              <input type="number" value={form.salary_min} onChange={e => set('salary_min', e.target.value)} placeholder="30000" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Salario máx</label>
              <input type="number" value={form.salary_max} onChange={e => set('salary_max', e.target.value)} placeholder="60000" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Moneda</label>
              <select value={form.salary_currency} onChange={e => set('salary_currency', e.target.value)} className={inputCls}>
                <option value="MXN">MXN</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Periodo</label>
              <select value={form.salary_period} onChange={e => set('salary_period', e.target.value)} className={inputCls}>
                <option value="monthly">Mensual</option>
                <option value="yearly">Anual</option>
                <option value="hourly">Por hora</option>
              </select>
            </div>
            <div className="flex items-center gap-2 pb-1">
              <input type="checkbox" id="show_salary" checked={form.show_salary} onChange={e => set('show_salary', e.target.checked)} className="accent-blue-500" />
              <label htmlFor="show_salary" className="text-white/60 text-xs">Mostrar</label>
            </div>
          </div>

          {/* Location + Type */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>Ubicación</label>
              <input type="text" value={form.location} onChange={e => set('location', e.target.value)} placeholder="CDMX" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Modalidad</label>
              <select value={form.remote_policy} onChange={e => set('remote_policy', e.target.value)} className={inputCls}>
                <option value="onsite">Presencial</option>
                <option value="hybrid">Híbrido</option>
                <option value="remote">Remoto</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Tipo empleo</label>
              <select value={form.employment_type} onChange={e => set('employment_type', e.target.value)} className={inputCls}>
                <option value="full_time">Tiempo completo</option>
                <option value="part_time">Medio tiempo</option>
                <option value="contract">Contrato</option>
                <option value="internship">Pasantía</option>
                <option value="freelance">Freelance</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Máx. candidatos</label>
              <input type="number" value={form.max_candidates} onChange={e => set('max_candidates', e.target.value)} placeholder="Sin límite" className={inputCls} />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-3 border-t border-white/10">
            <button type="button" onClick={onClose} className="px-4 py-2 text-white/60 hover:text-white text-sm">Cancelar</button>
            <button
              type="submit"
              disabled={saving || !form.title.trim() || !form.description.trim()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm rounded-lg font-medium transition-colors"
            >
              {saving ? 'Guardando...' : isEdit ? 'Guardar Cambios' : 'Crear Vacante'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export function VacanciesBoard() {
  const [vacancies, setVacancies] = useState<VacancyDTO[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editingVacancy, setEditingVacancy] = useState<VacancyDTO | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [vacData, statsData] = await Promise.all([
        getVacancies({ status: statusFilter || undefined, limit: 50 }),
        getVacancyStats(),
      ]);
      setVacancies(vacData.vacancies || []);
      setStats(statsData?.stats || statsData);
    } catch (e: any) {
      setError(e.message || 'Error al cargar vacantes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id: string) => setExpandedId(prev => prev === id ? null : id);

  const handleCreate = () => {
    setEditingVacancy(null);
    setShowForm(true);
  };

  const handleEdit = (v: VacancyDTO) => {
    setEditingVacancy(v);
    setShowForm(true);
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    setActionLoading(id);
    setError(null);
    try {
      await updateVacancyStatus(id, newStatus);
      await load();
    } catch (e: any) {
      setError(e.message || 'Error al cambiar estado');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (v: VacancyDTO) => {
    if (!window.confirm(`¿Eliminar la vacante "${v.title}"? Esta acción no se puede deshacer.`)) return;
    setActionLoading(v.id);
    setError(null);
    try {
      await deleteVacancy(v.id);
      await load();
    } catch (e: any) {
      setError(e.message || 'Error al eliminar vacante');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Briefcase className="text-blue-400" />
            Vacantes
          </h1>
          <p className="text-white/60 mt-1">Gestión de posiciones en la organización</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Plus size={14} />
            Nueva Vacante
          </button>
          <button onClick={load} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm disabled:opacity-50 transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Recargar
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Abiertas', val: stats.open || stats.by_status?.open || 0, color: 'text-emerald-400' },
            { label: 'Borradores', val: stats.draft || stats.by_status?.draft || 0, color: 'text-gray-400' },
            { label: 'Pausadas', val: stats.paused || stats.by_status?.paused || 0, color: 'text-amber-400' },
            { label: 'Total', val: stats.total || 0, color: 'text-blue-400' },
          ].map(s => (
            <div key={s.label} className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.val}</div>
              <div className="text-white/60 text-xs mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={14} className="text-white/40" />
        {['', 'open', 'draft', 'paused', 'closed', 'filled', 'cancelled'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              statusFilter === s
                ? 'bg-blue-500 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            {s ? (STATUS_CFG[s]?.label || s) : 'Todas'}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg">
          <AlertCircle size={16} />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300"><X size={14} /></button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <RefreshCw size={24} className="animate-spin mx-auto text-blue-400" />
          <p className="text-white/60 mt-2">Cargando vacantes...</p>
        </div>
      )}

      {/* Empty */}
      {!loading && vacancies.length === 0 && (
        <div className="text-center py-12 bg-white/5 border border-white/10 rounded-lg">
          <Briefcase size={48} className="mx-auto text-white/20 mb-4" />
          <h3 className="text-white text-lg font-medium">No hay vacantes</h3>
          <p className="text-white/60 mt-1">
            {statusFilter ? `No hay vacantes con estado "${STATUS_CFG[statusFilter]?.label || statusFilter}"` : 'No se encontraron vacantes'}
          </p>
          <button onClick={handleCreate} className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2">
            <Plus size={14} /> Crear primera vacante
          </button>
        </div>
      )}

      {/* List */}
      {!loading && vacancies.map(v => {
        const sc = STATUS_CFG[v.status] || STATUS_CFG.draft;
        const isExpanded = expandedId === v.id;
        const salary = formatSalary(v);
        const transitions = VALID_TRANSITIONS[v.status] || [];
        const canEdit = v.status === 'draft' || v.status === 'paused';
        const canDelete = v.status === 'draft' || v.status === 'cancelled';
        const isActing = actionLoading === v.id;

        return (
          <div key={v.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-colors">
            {/* Card header */}
            <button type="button" onClick={() => toggle(v.id)} className="w-full text-left p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-white font-semibold text-lg">{v.title}</h3>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                      {sc.icon} {sc.label}
                    </span>
                  </div>

                  {/* Tags row */}
                  <div className="flex items-center gap-3 text-white/60 text-sm flex-wrap mt-2">
                    {v.department && (
                      <span className="flex items-center gap-1"><Building2 size={13} /> {v.department}</span>
                    )}
                    {v.location && (
                      <span className="flex items-center gap-1"><MapPin size={13} /> {v.location}</span>
                    )}
                    <span className="flex items-center gap-1">
                      <Wifi size={13} /> {REMOTE_LABELS[v.remote_policy] || v.remote_policy}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={13} /> {EMPLOYMENT_LABELS[v.employment_type] || v.employment_type}
                    </span>
                    {salary && (
                      <span className="flex items-center gap-1 text-emerald-400">
                        <DollarSign size={13} /> {salary}
                      </span>
                    )}
                    {v.applications_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Users size={13} /> {v.applications_count} aplicaciones
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex-shrink-0 mt-1">
                  {isExpanded ? <ChevronUp size={18} className="text-white/40" /> : <ChevronDown size={18} className="text-white/40" />}
                </div>
              </div>
            </button>

            {/* Expanded details */}
            {isExpanded && (
              <div className="border-t border-white/10 p-5 bg-black/20 space-y-4">
                {/* Description */}
                <div>
                  <h4 className="text-white/80 font-medium text-sm mb-2">Descripción</h4>
                  <p className="text-white/70 text-sm whitespace-pre-wrap">{v.description}</p>
                </div>

                {/* Requirements */}
                {Array.isArray(v.requirements) && v.requirements.length > 0 && (
                  <div>
                    <h4 className="text-white/80 font-medium text-sm mb-2">Requisitos</h4>
                    <ul className="list-disc list-inside text-white/70 text-sm space-y-1">
                      {v.requirements.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}

                {/* Skills */}
                {Array.isArray(v.skills_required) && v.skills_required.length > 0 && (
                  <div>
                    <h4 className="text-white/80 font-medium text-sm mb-2">Skills requeridas</h4>
                    <div className="flex flex-wrap gap-2">
                      {v.skills_required.map((s, i) => (
                        <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-md text-xs">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Nice to have */}
                {Array.isArray(v.nice_to_have) && v.nice_to_have.length > 0 && (
                  <div>
                    <h4 className="text-white/80 font-medium text-sm mb-2">Deseable</h4>
                    <div className="flex flex-wrap gap-2">
                      {v.nice_to_have.map((s, i) => (
                        <span key={i} className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded-md text-xs">{s}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Generated copy */}
                {v.generated_copy && (
                  <div>
                    <h4 className="text-white/80 font-medium text-sm mb-2">Copy Generado por Orión</h4>
                    <div className="bg-black/30 border border-white/10 rounded-lg p-3 text-white/80 text-sm whitespace-pre-wrap">
                      {v.generated_copy}
                    </div>
                  </div>
                )}

                {/* Meta info */}
                <div className="flex items-center gap-4 text-white/50 text-xs pt-2 border-t border-white/10">
                  {v.deadline && (
                    <span className="flex items-center gap-1">
                      <Calendar size={12} /> Fecha límite: {new Date(v.deadline).toLocaleDateString('es-MX')}
                    </span>
                  )}
                  {v.created_at && (
                    <span>Creada: {new Date(v.created_at).toLocaleDateString('es-MX')}</span>
                  )}
                  {v.max_candidates && (
                    <span>Máx. candidatos: {v.max_candidates}</span>
                  )}
                </div>

                {/* ─── Action Buttons ─── */}
                <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-white/10">
                  {/* Status transitions */}
                  {transitions.length > 0 && (
                    <>
                      <span className="text-white/40 text-xs mr-1">Estado:</span>
                      {transitions.map(tr => {
                        const tcfg = STATUS_CFG[tr.to];
                        return (
                          <button
                            key={tr.to}
                            onClick={() => handleStatusChange(v.id, tr.to)}
                            disabled={isActing}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${tcfg.bg} ${tcfg.color} hover:opacity-80 disabled:opacity-50`}
                          >
                            {isActing ? <RefreshCw size={12} className="animate-spin" /> : tr.icon}
                            {tr.label}
                          </button>
                        );
                      })}
                    </>
                  )}

                  <div className="ml-auto flex items-center gap-2">
                    {/* Edit */}
                    {canEdit && (
                      <button
                        onClick={() => handleEdit(v)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
                      >
                        <Pencil size={12} /> Editar
                      </button>
                    )}

                    {/* Delete */}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(v)}
                        disabled={isActing}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-50 transition-colors"
                      >
                        <Trash2 size={12} /> Eliminar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Form Modal */}
      {showForm && (
        <VacancyFormModal
          initial={editingVacancy ? vacancyToForm(editingVacancy) : EMPTY_FORM}
          editId={editingVacancy?.id || null}
          onClose={() => { setShowForm(false); setEditingVacancy(null); }}
          onSaved={() => { setShowForm(false); setEditingVacancy(null); load(); }}
        />
      )}
    </div>
  );
}

export default VacanciesBoard;
